import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { BucketStrategy, camelToDisplay, Milliseconds, roundDecimals } from '@iotv/datamodel';
import { Box, Button, Slider, Stack, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useState } from 'react';
import config from '../../../config';
import { LineBarChart } from './LineBarChart';
import { historyObjectToDataTable } from './transforms';
const debug = process.env.REACT_APP_DEBUG && false;
const defaultKeyNameMapper = (k) => camelToDisplay(k);
export const RangeableLineBarChart = ({ historyObject, keys, timeSliceButtonValues, heightIn, bucketStrategyMap, keyNameMapper = defaultKeyNameMapper, fragmentProps = {}, chartAreaProps = {}, legendConfig = {}, reverseSlicing = false }) => {
    const [maxIdx, setMaxIdx] = useState(0);
    const [slice, setSlice] = useState([0, 0]);
    const historyStoreLength = historyObject.timestamp.length - 1;
    /** debug */
    const firstTimestamp = historyObject.timestamp.at(0) ?? 0;
    const lastTimestamp = historyObject.timestamp.at(historyObject.timestamp.length - 1) ?? 0;
    const historyDurationHrs = (lastTimestamp - firstTimestamp) / Milliseconds.HOUR;
    debug && console.log(`RangeableLineBarChart ( with duration ${roundDecimals(historyDurationHrs, 1)} hrs)`);
    useEffect(() => {
        debug && console.log(`RangeableLineBarChart used effect on ${historyStoreLength} length`);
        setMaxIdx(historyStoreLength);
        setSlice([0, historyStoreLength]);
    }, [historyStoreLength]);
    //debug && slice instanceof Array && console.log(`length: ${historyObject.index.length} start: ${slice[0]} end: ${slice[1]} sliceLength: ${slice[1] - slice[0]} `)
    const validSlice = slice instanceof Array ? slice : [0, slice];
    const [start, end] = validSlice;
    const latest = maxIdx - start;
    const oldest = maxIdx - end;
    const processedSlice = [oldest, latest];
    const selectedSlice = reverseSlicing ? processedSlice : validSlice;
    const data = historyObjectToDataTable(historyObject, keys, selectedSlice);
    /** https://developers.google.com/chart/interactive/docs/gallery/combochart - there seems to be no typeing for series */
    const options = {
        chartArea: chartAreaProps,
        legend: legendConfig,
        seriesType: 'bars',
        interpolateNulls: false,
        bar: { groupWidth: 90 },
        series: Object.fromEntries(keys.map((k, i) => {
            const bucketStrategy = bucketStrategyMap?.[k] ?? BucketStrategy.INFERREADING;
            const s = {
                labelInLegend: keyNameMapper(k),
                type: bucketStrategy === BucketStrategy.APPORTION ? 'bars' : 'line',
            };
            return [i, s];
        })),
    };
    const setTimeSlice = (agoMs) => {
        const thenMs = Date.now() - agoMs;
        const lowIdx = historyObject.timestamp.findIndex((ts) => ts > thenMs);
        debug && console.log('lowIdx', lowIdx);
        setSlice([historyObject.index.length, lowIdx]);
    };
    // this looks like a MUI bug it doesn't work on the demo page.
    const valuetext = (idx, idx2) => {
        const label = new Date(historyObject.timestamp[idx] * 1000).toLocaleDateString(config.locale);
        //debug && console.log('value text has', {idx, idx2, label})
        return label;
    };
    const handleSliceChange = (event, value, activeThumb) => {
        debug && console.log('handleSliceChange', { event, value, activeThumb });
        if (value instanceof Array && value.length === 2) {
            const newValue = value;
            setSlice(newValue);
        }
    };
    const sliderProps = {
        max: maxIdx,
        min: 0,
        value: slice,
        onChange: handleSliceChange,
        valueLabelDisplay: 'auto',
        'aria-labelledby': 'range-slider',
        getAriaValueText: (idx1, idx2) => valuetext(idx1, idx2),
    };
    const getSliceSelectButton = (count, unit, key) => (_jsxs(Button, { onClick: () => {
            setTimeSlice(count * unit.valueOf());
        }, children: [count, " ", Milliseconds[unit]] }, key));
    return (_jsx(Box, { height: '100%', children: data.getNumberOfRows() > 0 && data.getNumberOfColumns() > 1 ? (_jsxs(Box, { height: '100%', children: [_jsx(LineBarChart, { data, options, fragmentProps }), _jsxs(Box, { paddingInline: 2, width: chartAreaProps.width, children: [_jsx(Slider, { ...sliderProps }), timeSliceButtonValues?.map(([count, unit], i) => getSliceSelectButton(count, unit, i))] })] })) : (_jsxs(Stack, { height: heightIn ?? fragmentProps.style?.height ?? '100%', children: [_jsx(Skeleton, { variant: "rectangular", sx: { flexGrow: 1 } }), data.getNumberOfColumns() < 2 && (_jsx(Stack, { paddingBlock: 1, alignItems: "center", children: _jsx(Typography, { color: "GreyText", children: "No Columns Selected" }) })), _jsx(Skeleton, { variant: "rectangular", sx: { flexGrow: 1 } })] })) }));
};
